import React, { useCallback, useEffect, useRef } from 'react';
import { Text, View } from 'react-native';
import { FlashList, ListRenderItem } from '@shopify/flash-list';

import { ColorFunction } from '@styles/colors/types';
import { fullWidth, scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import Image from '@v2/context/Image/context';
import TouchableElement from '@components/common/touchableElement';
import Loading from '@components/loading';
import { ImageParams } from '@interfaces/search';
import { isWeb } from '@helpers';

export interface SimpleSearchLineWrapperProps<T> {
  searchResult: T[];
  getImageParams: (item: T) => ImageParams;
  onItemSelect: (item: T) => void;
  keyExtractor: (item: T) => React.Key;
  getText: (item: T) => string;
  C: ColorFunction;
  isPopupMode: boolean;
  popupWidth: number;
  loaded: boolean;
  attachMode: boolean;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  width?: number;
}

const SimpleSearchLineWrapper = <T,>({
  searchResult,
  isPopupMode,
  popupWidth,
  width,
  C,
  loaded,
  onItemSelect,
  getImageParams,
  attachMode,
  keyExtractor,
  getText,
  scale = initialScale,
  font = initialFont,
}: SimpleSearchLineWrapperProps<T>) => {
  const effectiveWidth = isPopupMode && popupWidth ? popupWidth : fullWidth;
  const searchSessionRef = useRef<{ isLoaded: boolean; data: T[] }>({ isLoaded: false, data: [] });

  // Track search sessions to prevent showing stale data during React batching (mobile only)
  useEffect(() => {
    if (!isWeb) {
      if (loaded) {
        // When loaded becomes true, update the session with current data
        searchSessionRef.current = { isLoaded: true, data: searchResult };
      } else {
        // When loaded becomes false, mark session as not loaded but keep previous data until new data arrives
        searchSessionRef.current = { isLoaded: false, data: searchSessionRef.current.data };
      }
    }
  }, [loaded, searchResult]);

  // Determine what to show: for web use simple logic, for mobile use session tracking
  const shouldShowData = isWeb ? loaded : (loaded && searchSessionRef.current.isLoaded);
  const dataToShow = shouldShowData ? searchResult : [];

  const renderItem: ListRenderItem<T> = useCallback(
    ({ item }) => (
      <TouchableElement onPress={() => onItemSelect(item)}>
        {({ pressHoverColor, boxStyle }) => (
          <View
            style={[
              boxStyle,
              {
                backgroundColor: C(pressHoverColor('Background/Light/System')),
                height: scale(60),
                paddingVertical: scale(8),
                flexDirection: 'row',
                gap: scale(10),
                alignItems: 'center',
                borderRadius: scale(10),
              },
            ]}
          >
            <Image onPress={() => onItemSelect(item)} image={getImageParams(item)} attachMode={attachMode} />
            <Text numberOfLines={1} style={[font['Button 1'], { color: C('Label/Light/Primary'), flex: 1 }]}>
              {getText(item)}
            </Text>
          </View>
        )}
      </TouchableElement>
    ),
    [C, attachMode, font, getImageParams, getText, onItemSelect, scale]
  );

  return (
    <View style={{ width: width ?? effectiveWidth, flex: 1, paddingHorizontal: scale(15) }}>
      {shouldShowData ? (
        <FlashList
          data={dataToShow}
          keyExtractor={(item) => String(keyExtractor(item))}
          renderItem={renderItem}
          estimatedItemSize={scale(60)}
          showsVerticalScrollIndicator={false}
        />
      ) : (
        <Loading />
      )}
    </View>
  );
};

export default SimpleSearchLineWrapper;
